'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX, MouseEvent as ReactMouseEvent } from 'react';
import { AnimatedSwitch } from '@/components/ui/animated-switch';
import { DropdownMenuItem } from '@/components/ui/app-dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { ConstructionIcon } from '@/components/ui/icon/glyphs';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { isMainnetEnabled } from '@/lib/network/mainnet-enabled';
import { useSwitchNetwork } from '@/lib/network/switcher';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { useMainnetConstructionModalStore } from '@/stores/mainnet-construction-modal';
import { useNetworkTransitionStore } from '@/stores/network-transition';

/** Switch visibility and mainnet availability have independent rollout gates. */
export function DemoModeToggle() {
  const $ = _c(20);
  const t = useTranslations("header.wallet");
  const showMainnetSwitch = useFeatureGate("mainnet_switch") === true;
  const networkMode = useNetworkMode();
  const switchNetwork = useSwitchNetwork();
  const openMainnetConstructionModal = useMainnetConstructionModalStore(_temp);
  const transitionInFlight = useNetworkTransitionStore(_temp2);
  if (!showMainnetSwitch) {
    return null;
  }
  const isDevnet = networkMode === "devnet";
  let t0;
  if ($[0] !== isDevnet || $[1] !== openMainnetConstructionModal || $[2] !== switchNetwork || $[3] !== transitionInFlight) {
    t0 = () => {
      if (transitionInFlight) {
        return;
      }
      const nextNetwork = isDevnet ? "mainnet" : "devnet";
      if (nextNetwork === "mainnet" && !isMainnetEnabled()) {
        openMainnetConstructionModal(true);
        return;
      }
      switchNetwork(nextNetwork);
    };
    $[0] = isDevnet;
    $[1] = openMainnetConstructionModal;
    $[2] = switchNetwork;
    $[3] = transitionInFlight;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleToggle = t0;
  let t1;
  if ($[5] !== t) {
    t1 = t("demo_mode");
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const label = t1;
  let t2;
  if ($[7] !== t) {
    t2 = t("demo_mode_aria_label");
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const ariaLabel = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={ConstructionIcon} className="size-4" />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== label) {
    t4 = <span className="flex items-center gap-2">{t3}{label}</span>;
    $[10] = label;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== ariaLabel || $[13] !== handleToggle || $[14] !== isDevnet || $[15] !== transitionInFlight) {
    t5 = <AnimatedSwitch size="sm" variant="brand" checked={isDevnet} disabled={transitionInFlight} onCheckedChange={handleToggle} aria-label={ariaLabel} onClick={_temp4} />;
    $[12] = ariaLabel;
    $[13] = handleToggle;
    $[14] = isDevnet;
    $[15] = transitionInFlight;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <DropdownMenuItem data-testid="demo-mode-row" onSelect={_temp3} className="flex items-center justify-between gap-3">{t4}{t5}</DropdownMenuItem>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e) {
  return e.preventDefault();
}
function _temp2(s_0) {
  return s_0.from !== null;
}
function _temp(s) {
  return s.setOpen;
}